import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useRef, useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { DatosParaPagar } from "@/componentes/catalogos/DatosParaPagar"
import { DiferenciaPago } from "@/componentes/ordenes/DiferenciaPago"
import { type Lectura, LecturaComprobante } from "@/componentes/ordenes/LecturaComprobante"
import { precioLineaValido, precioValido } from "@/componentes/ordenes/lineas"
import type { Orden, TratamientoDiferencia } from "@/componentes/ordenes/tipos"
import { Conversor } from "@/componentes/tesoreria/Conversor"
import { esCuentaBloqueada, PinEnLinea } from "@/componentes/tesoreria/CuentaBloqueada"
import { aCentimos, comoMonto, monto as monto_ } from "@/componentes/tesoreria/dinero"
import { monedasDeTasas, type TasaVigente } from "@/componentes/tesoreria/tasas"
import type { Cuenta } from "@/componentes/tesoreria/tipos"
import { useTransferirFondos } from "@/componentes/tesoreria/useTransferirFondos"
import {
  type Adjuntando,
  AdjuntosNuevos,
  archivosPegados,
  sumar,
} from "@/componentes/ui/AdjuntosNuevos"
import { Boton } from "@/componentes/ui/Boton"
import { Modal } from "@/componentes/ui/Modal"
import { SelectorBuscable } from "@/componentes/ui/SelectorBuscable"
import { tienePermiso } from "@/lib/autorizacion"
import { comoFecha } from "@/lib/fechas"
import { usarSesion } from "@/lib/sesion"
import { Campo } from "@/paginas/interno/Gastos"

type MetodoPago = { id: number; nombre: string; descripcion: string | null; activo: boolean }

type Propuesta = {
  divisa_orden: string
  divisa_cuenta: string
  monto_orden: string
  /** Lo que falta por pagar de la orden, en su moneda. */
  monto_pendiente: string
  /** El crédito del proveedor en la moneda de la orden. */
  saldo_a_favor: string
  monto_debitado: string
  tasa_aplicada: string | null
  comision: string
  aplica_igtf: boolean
  monto_igtf: string
  saldo_disponible: string
  /** Lo que habria que traer para cubrir comision e IGTF. `null` si no hay cambio
   *  de recursos vivo que continuar. */
  comision_a_traer: string | null
  cuenta_comision: string | null
  divisa_comision: string | null
  /** Si la cuenta admite quedar en negativo. Ver `CuentaBancaria.permite_sobregiro`. */
  permite_sobregiro: boolean
  /** Si la cuenta trae el ingreso que fondea cada pago. Ver `fondeo_automatico`. */
  fondeo_automatico?: boolean
}

/** Hoy en la zona del navegador, en `AAAA-MM-DD`: lo que espera un `<input type="date">`. */
function hoy(): string {
  const d = new Date()
  const dos = (n: number) => String(n).padStart(2, "0")
  return `${d.getFullYear()}-${dos(d.getMonth() + 1)}-${dos(d.getDate())}`
}

/**
 * Ejecutar el pago de una orden aprobada: el dinero sale del banco.
 *
 * ⚠ **Lo que propone el sistema es una SUGERENCIA; lo que vale es lo que el banco
 * debitó.** Al elegir cuenta y método se precargan el monto —convertido a la tasa
 * vigente si la cuenta es de otra moneda—, la comisión y el IGTF. Se corrigen con lo
 * que diga el comprobante: convertir solo sería decidir por tesorería el precio de una
 * operación cambiaria.
 *
 * ⚠ **El comprobante es obligatorio y viaja en la MISMA petición que el pago.** Un pago
 * sin respaldo no se registra, y dos peticiones dejarían, si falla la segunda, un
 * débito sin comprobante. Se elige, se arrastra o se pega con Ctrl+V: la captura de
 * la transferencia suele estar recién hecha en el portapapeles.
 *
 * ⚠ **Sólo se ofrecen los métodos que sirven para la moneda de la cuenta.** El
 * catálogo guarda la tarifa en SU moneda —el pago móvil cobra 14 Bs—, así que
 * ofrecer Zelle desde una cuenta en bolívares sólo sirve para que el servidor lo
 * rechace después con un 409.
 *
 * ⚠ **La tasa es la de la FECHA VALOR del pago, no la de hoy.** Un pago del 24 de agosto
 * ejecutado el 27 de septiembre se convierte al cambio del 24: la propuesta y el
 * conversor piden las tasas de esa fecha, y cambiarla las vuelve a pedir.
 *
 * ⚠ **Con la orden y la cuenta en la misma moneda no hay conversor.** No hay nada que
 * convertir: se debita el neto. Enseñarlo invitaba a elegir otra moneda de referencia,
 * y el ir y volver por un importe en dólares redondeado a dos decimales CAMBIABA lo
 * debitado —19.883,36 Bs se volvían 19.757,82—.
 *
 * ⚠ **La comisión y el IGTF admiten CERO.** Una transferencia del mismo banco no cobra
 * comisión; validarla como un precio —mayor que cero— dejaba el botón apagado sin
 * decir por qué, mientras que con pago móvil, que sí cobra, funcionaba.
 *
 * ⚠ **El saldo se mira contra el TOTAL que sale** —pago, comisión e IGTF—, no contra el
 * pago solo: con eso, el banco lo rechazaría por unos bolívares de comisión.
 *
 * ⚠ **Lo tecleado en el monto NO se pisa al cambiar la fecha.** Cambiar la fecha pide
 * otra propuesta —la tasa es la de ese día— y la propuesta nueva rellenaba el monto con
 * el neto de la orden: en OP-2026-0004 se tecleó lo que debitó el banco, se corrigió la
 * fecha después y se asentó la cifra de la orden. Sólo otra CUENTA vuelve a precargar,
 * porque entonces lo tecleado era para otra cuenta.
 *
 * ⚠ **Pagar otra cifra que la pendiente se DECIDE** (`DiferenciaPago`): cerrar, dejar
 * el resto como saldo a favor del proveedor o el excedente como saldo disponible de la
 * empresa frente a él. Sin decidir no se ejecuta.
 *
 * ⚠ **El comprobante se lee con IA al adjuntarlo** (`LecturaComprobante`), y lo leído
 * sólo entra al formulario con «Usar estos datos».
 *
 * ⚠ **Al elegir el método salen los datos del proveedor que le sirven** (`DatosParaPagar`,
 * 03/11): su cuenta, su pago móvil, su Zelle, para copiarlos al banco.
 */
export function ModalEjecutarPago({ orden, onCerrar }: { orden: Orden; onCerrar: () => void }) {
  const clienteQuery = useQueryClient()
  const [cuentaId, setCuentaId] = useState<number | null>(null)
  const [metodoId, setMetodoId] = useState<number | null>(null)
  const [monto, setMonto] = useState("")
  const [comision, setComision] = useState("0")
  const [aplicaIgtf, setAplicaIgtf] = useState(false)
  const [igtf, setIgtf] = useState("0")
  const [referencia, setReferencia] = useState("")
  const [fecha, setFecha] = useState(hoy())
  const [comprobante, setComprobante] = useState<Adjuntando[]>([])
  // El pago que no produce comprobante —un cruce de fondos entre cuentas propias— y
  // por qué no lo produce. Ver `tesoreria:ejecutar_sin_comprobante`.
  // Por omision SI se trae: dejarlo apagado repetiria el descuadre que esto viene a
  // cerrar, y quien no lo quiera lo desmarca viendo el importe.
  const [traerComision, setTraerComision] = useState(true)
  const [conFondeo, setConFondeo] = useState(true)
  const [sinComprobante, setSinComprobante] = useState(false)
  const [motivoSinComprobante, setMotivoSinComprobante] = useState("")
  const [rechazados, setRechazados] = useState<string[]>([])
  // Lo tecleado —o confirmado desde la lectura— manda sobre la propuesta.
  const montoTocado = useRef(false)
  // Con otra moneda: lo que cubre el pago en la moneda de la orden.
  const [equivalente, setEquivalente] = useState("")
  const [tratamiento, setTratamiento] = useState<TratamientoDiferencia | null>(null)
  const usuario = usarSesion((e) => e.usuario)
  const puedeLeer = tienePermiso(usuario, "factura_ia:usar")
  // Cosmético, como todo `tienePermiso`: sin él, el servidor responde 409 igual.
  const puedeSinComprobante = tienePermiso(usuario, "tesoreria:ejecutar_sin_comprobante")

  const cuentas = useQuery({
    queryKey: ["cuentas-bancarias"],
    queryFn: () => api.get<Cuenta[]>("/api/v1/cuentas-bancarias"),
  })
  const cuenta = (cuentas.data ?? []).find((c) => c.id === cuentaId) ?? null
  const metodos = useQuery({
    queryKey: ["metodos-pago", "activos", cuenta?.divisa_id ?? null],
    queryFn: () =>
      api.get<MetodoPago[]>(
        `/api/v1/metodos-pago?activo=true${cuenta ? `&divisa_id=${cuenta.divisa_id}` : ""}`,
      ),
  })
  // En la lectura natural: bolívares por una unidad. Es la del conversor.
  // Las del día del PAGO, no las de hoy. Sin fecha todavía, las vigentes.
  const tasas = useQuery({
    queryKey: ["tasas-vigentes", fecha],
    queryFn: () =>
      api.get<TasaVigente[]>(`/api/v1/tasas/vigentes${fecha ? `?fecha=${fecha}` : ""}`),
  })
  const conFecha = fecha ? `&fecha_valor=${fecha}` : ""
  const propuesta = useQuery({
    queryKey: ["propuesta-pago", orden.id, cuentaId, metodoId, fecha],
    queryFn: () =>
      api.get<Propuesta>(
        `/api/v1/ordenes-pago/${orden.id}/propuesta-pago?cuenta_id=${cuentaId}${
          metodoId !== null ? `&metodo_pago_id=${metodoId}` : ""
        }${conFecha}`,
      ),
    enabled: cuentaId !== null,
    // Otra fecha pide otra propuesta; mientras llega, los importes siguen a la vista en
    // vez de desaparecer y volver —con lo tecleado dentro—.
    placeholderData: keepPreviousData,
  })

  // Cambiar de cuenta puede dejar elegido un método de otra moneda —pago móvil con
  // una cuenta en dólares—, y el servidor lo rechazaría al pagar.
  useEffect(() => {
    if (metodoId === null || metodos.data === undefined) return
    if (!metodos.data.some((m) => m.id === metodoId)) setMetodoId(null)
  }, [metodos.data, metodoId])

  // Cada propuesta nueva vuelve a precargar comisión e IGTF, y el monto sólo si nadie
  // lo tecleó: cambiar la FECHA no puede borrar lo que dijo el banco (ver la cabecera).
  useEffect(() => {
    const p = propuesta.data
    if (!p) return
    if (!montoTocado.current) {
      setMonto(p.monto_debitado)
      setEquivalente(p.monto_pendiente)
    }
    setComision(p.comision)
    setAplicaIgtf(p.aplica_igtf)
    setIgtf(p.monto_igtf)
  }, [propuesta.data])

  // ⚠ **La comisión y el IGTF siguen al monto, porque dependen de él**: 0,3 % del
  // importe, 3 % del importe. Corregir lo debitado y dejarlas con el valor del
  // importe propuesto es un descuadre que nadie nota, porque las cifras viejas
  // siguen pareciendo razonables. Se recalculan en el SERVIDOR, con la misma función
  // que las propuso: la tarifa de un método es porcentaje, fijo, suelo, techo, quién
  // la asume e IVA del banco, y una segunda copia de esa cuenta aquí discreparía.
  //
  // Medio segundo después de dejar de teclear, y sólo si el importe cambió: una
  // petición por pulsación sería un cálculo distinto en cada letra de la cifra.
  const [montoDiferido, setMontoDiferido] = useState<string | null>(null)
  useEffect(() => {
    const valido = precioValido(monto)
    if (valido === null || propuesta.data === undefined) return
    if (valido === propuesta.data.monto_debitado) {
      setMontoDiferido(null)
      return
    }
    const id = setTimeout(() => setMontoDiferido(valido), 500)
    return () => clearTimeout(id)
  }, [monto, propuesta.data])

  const recalculo = useQuery({
    queryKey: ["propuesta-pago", orden.id, cuentaId, metodoId, fecha, montoDiferido],
    queryFn: () =>
      api.get<Propuesta>(
        `/api/v1/ordenes-pago/${orden.id}/propuesta-pago?cuenta_id=${cuentaId}${
          metodoId !== null ? `&metodo_pago_id=${metodoId}` : ""
        }&monto_debitado=${montoDiferido}${conFecha}`,
      ),
    enabled: cuentaId !== null && montoDiferido !== null,
  })

  useEffect(() => {
    const r = recalculo.data
    if (!r) return
    setComision(r.comision)
    setIgtf(r.monto_igtf)
  }, [recalculo.data])

  const ejecutar = useMutation({
    meta: {
      exito: { titulo: "Pago registrado", texto: `Se registró el pago de ${orden.codigo}.` },
    },
    mutationFn: () => {
      const datos = new FormData()
      datos.append(
        "datos",
        JSON.stringify({
          cuenta_id: cuentaId,
          metodo_pago_id: metodoId,
          monto_debitado: precioValido(monto),
          comision: comision.trim() === "" ? "0" : precioLineaValido(comision),
          monto_igtf: aplicaIgtf ? precioLineaValido(igtf) : "0",
          referencia: referencia.trim(),
          fecha_valor: fecha,
          monto_equivalente: mismaMoneda ? null : precioValido(equivalente),
          motivo_sin_comprobante: sinComprobante ? motivoSinComprobante.trim() : null,
          tratamiento_diferencia: diferencia !== null && diferencia !== 0n ? tratamiento : null,
          traer_comision: puedeTraerComision && traerComision,
          // Sólo se manda cuando la cuenta lo trae y se dice que NO: en una cuenta
          // normal el servidor ya decide que no, y mandarlo sería ruido.
          ...(propuesta.data?.fondeo_automatico && !conFondeo ? { con_fondeo: false } : {}),
        }),
      )
      if (!sinComprobante && comprobante[0]) {
        datos.append("comprobante", comprobante[0].archivo)
      }
      return api.post<Orden>(`/api/v1/ordenes-pago/${orden.id}/ejecutar`, datos)
    },
    onSuccess: () => {
      clienteQuery.invalidateQueries({ queryKey: ["orden-pago", orden.id] })
      clienteQuery.invalidateQueries({ queryKey: ["ordenes-pago"] })
      clienteQuery.invalidateQueries({ queryKey: ["cuentas-bancarias"] })
      onCerrar()
    },
  })

  // El total que sale de la cuenta, en céntimos exactos.
  // Comisión e IGTF admiten cero (ver la cabecera); lo debitado, no.
  const partes = [precioValido(monto), comision.trim() === "" ? "0" : precioLineaValido(comision)]
  if (aplicaIgtf) partes.push(precioLineaValido(igtf))
  const validos = partes.every((p) => p !== null)
  const total = validos ? partes.reduce((s, p) => s + aCentimos(p ?? "0"), 0n) : null
  const saldo = propuesta.data ? aCentimos(propuesta.data.saldo_disponible) : null
  // ⚠ **Una cuenta de sobregiro puede quedar en negativo, y el botón tiene que
  // dejarla.** `ejecucion_pagos.ejecutar` sólo exige saldo cuando
  // `permite_sobregiro` es falso; aquí se comparaba el total con el saldo a secas, así
  // que Binance —marcada para sobregiro justo porque de ahí se adelanta el gasto de la
  // oficina— quedaba sin poder pagar con el servidor diciendo que sí. El aviso se
  // queda: quedarse corto sigue siendo casi siempre un movimiento que falta.
  const sobregiro = propuesta.data?.permite_sobregiro === true

  // El servidor es quien decide si hay cambio que continuar: manda `comision_a_traer`
  // sólo cuando existe uno vivo que entró en ESTA cuenta. La pantalla no lo adivina.
  const puedeTraerComision =
    propuesta.data?.comision_a_traer != null && aCentimos(propuesta.data.comision_a_traer) > 0n
  // ⚠ **Lo que se trae de la otra cuenta ENTRA antes de debitar, y cuenta para el
  // saldo.** El servidor trae comisión + IGTF y DESPUÉS mira si alcanza
  // (`ejecucion_pagos.ejecutar`); la pantalla comparaba con el saldo de antes, decía
  // «no alcanza» y apagaba el botón de un pago que el servidor sí aceptaba (07/10:
  // OP-2026-0133, 26.215,80 de saldo, 26.294,45 de salida con 78,65 que entraban).
  const entraDeOtraCuenta =
    puedeTraerComision && traerComision && validos
      ? aCentimos(comision.trim() === "" ? "0" : (precioLineaValido(comision) ?? "0")) +
        (aplicaIgtf ? aCentimos(precioLineaValido(igtf) ?? "0") : 0n)
      : 0n
  const disponible = saldo === null ? null : saldo + entraDeOtraCuenta
  const corto = total !== null && disponible !== null && total > disponible
  const alcanza = total !== null && disponible !== null && (!corto || sobregiro)
  const moneda = propuesta.data?.divisa_cuenta ?? ""
  // ⚠ **Si la cuenta no alcanza, lo primero es traer el dinero** (19/10): el aviso
  // ofrece la transferencia sin salir del pago. Falta lo del PAGO; la comisión la trae
  // después «Traer también la comisión», con la tasa de ese cambio. Si el pago cabe y
  // sólo falta la comisión, falta eso.
  const debitadoC = precioValido(monto) === null ? null : aCentimos(precioValido(monto) ?? "0")
  const faltante =
    corto && !sobregiro && disponible !== null && total !== null
      ? debitadoC !== null && debitadoC > disponible
        ? debitadoC - disponible
        : total - disponible
      : null
  const fondos = useTransferirFondos({
    cuentas: cuentas.data ?? [],
    cuentaPagaId: cuentaId,
    faltante,
    moneda,
    ordenIds: [orden.id],
  })
  const mismaMoneda =
    propuesta.data !== undefined && propuesta.data.divisa_orden === propuesta.data.divisa_cuenta

  // Lo que cubre el pago en la moneda de la ORDEN, contra lo que falta. En céntimos.
  const cubre = mismaMoneda ? precioValido(monto) : precioValido(equivalente)
  const diferencia =
    propuesta.data && cubre !== null
      ? aCentimos(cubre) - aCentimos(propuesta.data.monto_pendiente)
      : null
  const decidido =
    diferencia === null ||
    diferencia === 0n ||
    (diferencia < 0n
      ? tratamiento === "CERRAR" || tratamiento === "SALDO_PROVEEDOR"
      : tratamiento === "CERRAR" || tratamiento === "SALDO_EMPRESA")
  const saldoFavor = propuesta.data ? aCentimos(propuesta.data.saldo_a_favor) : 0n
  const parcial =
    propuesta.data !== undefined && propuesta.data.monto_pendiente !== propuesta.data.monto_orden

  const aplicarSaldo = useMutation({
    meta: { exito: { titulo: "Saldo a favor aplicado", texto: orden.codigo } },
    mutationFn: (cuanto: string) =>
      api.post<Orden>(`/api/v1/ordenes-pago/${orden.id}/aplicar-saldo-favor`, {
        monto: cuanto,
        fecha_valor: fecha,
      }),
    onSuccess: () => {
      clienteQuery.invalidateQueries({ queryKey: ["orden-pago", orden.id] })
      clienteQuery.invalidateQueries({ queryKey: ["ordenes-pago"] })
      onCerrar()
    },
  })

  function usarLectura(lectura: Lectura, usarMonto: boolean) {
    const leido = lectura.comprobante
    // Cuenta y método antes que el monto: el monto confirmado queda «tecleado» y la
    // propuesta de la cuenta nueva ya no lo pisa.
    if (lectura.cuenta_sugerida) setCuentaId(lectura.cuenta_sugerida.id)
    if (lectura.metodo_sugerido) setMetodoId(lectura.metodo_sugerido.id)
    if (usarMonto && leido.monto) {
      setMonto(leido.monto)
      montoTocado.current = true
    }
    if (leido.comision) setComision(leido.comision)
    if (leido.referencia) setReferencia(leido.referencia.slice(0, 60))
    if (leido.fecha) setFecha(leido.fecha)
  }

  const listo =
    cuentaId !== null &&
    precioValido(monto) !== null &&
    validos &&
    alcanza &&
    decidido &&
    referencia.trim().length >= 3 &&
    fecha !== "" &&
    // O el archivo, o el motivo de que no lo haya: nunca ninguno de los dos.
    (sinComprobante ? motivoSinComprobante.trim().length >= 10 : comprobante.length === 1)

  const error =
    ejecutar.error instanceof ErrorApi && !esCuentaBloqueada(ejecutar.error)
      ? ejecutar.error.message
      : null

  return (
    <>
      {fondos.dialogo}
      <Modal
        abierto={!fondos.transfiriendo}
        titulo={`Ejecutar pago · ${orden.codigo}`}
        descripcion={`${orden.proveedor ?? ""} · ${
          orden.monto_pendiente && orden.monto_pendiente !== orden.monto_neto
            ? `pendiente ${monto_(orden.monto_pendiente)} de ${monto_(orden.monto_neto)}`
            : `a pagar ${monto_(orden.monto_neto)}`
        } ${orden.divisa ?? ""}`}
        onCerrar={onCerrar}
      >
        <form
          className="space-y-4"
          onSubmit={(evento) => {
            evento.preventDefault()
            if (listo) ejecutar.mutate()
          }}
          onPaste={(evento) => {
            // Pegar texto en «referencia» tiene que seguir funcionando: sólo se
            // intercepta cuando lo que viene son archivos.
            const archivos = archivosPegados(evento.clipboardData, 0)
            if (archivos.length === 0) return
            evento.preventDefault()
            const siguiente = sumar([], archivos.slice(0, 1))
            setComprobante(siguiente.adjuntos)
            setRechazados(siguiente.rechazados)
          }}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <Campo para="pago-cuenta" etiqueta="Cuenta que paga">
              <SelectorBuscable
                id="pago-cuenta"
                opciones={(cuentas.data ?? [])
                  .filter((c) => c.activo)
                  .map((c) => ({
                    valor: c.id,
                    etiqueta: c.nombre,
                    detalle: [c.banco, c.ultimos4 ? `···${c.ultimos4}` : null]
                      .filter(Boolean)
                      .join(" "),
                  }))}
                valor={cuentaId}
                onCambiar={(id) => {
                  // Otra cuenta: lo tecleado era para la anterior y se vuelve a proponer.
                  montoTocado.current = false
                  setCuentaId(id)
                }}
                placeholder="Buscar cuenta…"
              />
            </Campo>
            <Campo para="pago-metodo" etiqueta="Método de pago">
              <SelectorBuscable
                id="pago-metodo"
                opciones={(metodos.data ?? []).map((m) => ({
                  valor: m.id,
                  etiqueta: m.nombre,
                  detalle: m.descripcion ?? undefined,
                }))}
                valor={metodoId}
                onCambiar={setMetodoId}
                vacio="Sin especificar"
              />
            </Campo>
          </div>

          {/* Los datos del proveedor que sirven al método elegido (03/11, pedido del
              usuario): la cuenta para una transferencia, el teléfono para un pago móvil.
              La `key` por método vuelve a «sólo los de este método» al cambiarlo. */}
          <DatosParaPagar
            key={metodoId ?? "sin-metodo"}
            proveedorId={orden.proveedor_id}
            metodo={{ id: metodoId, nombre: metodos.data?.find((m) => m.id === metodoId)?.nombre }}
          />

          {propuesta.data && saldoFavor > 0n && (
            <div className="tarjeta flex flex-wrap items-center gap-2 p-3 text-sm">
              <span className="flex-1">
                La empresa tiene <strong className="tabular-nums">{comoMonto(saldoFavor)}</strong>{" "}
                {propuesta.data.divisa_orden} de saldo disponible frente a{" "}
                {orden.proveedor ?? "este proveedor"}, de pagos anteriores.
              </span>
              <Boton
                tamano="sm"
                disabled={aplicarSaldo.isPending || fecha === ""}
                onClick={() => {
                  const pendiente = aCentimos(propuesta.data?.monto_pendiente ?? "0")
                  const cuanto = saldoFavor < pendiente ? saldoFavor : pendiente
                  aplicarSaldo.mutate(`${cuanto / 100n}.${String(cuanto % 100n).padStart(2, "0")}`)
                }}
              >
                {aplicarSaldo.isPending ? "Aplicando…" : "Aplicar a esta orden"}
              </Boton>
              {aplicarSaldo.error instanceof ErrorApi && (
                <p className="w-full" style={{ color: "var(--destructive)" }}>
                  {aplicarSaldo.error.message}
                </p>
              )}
            </div>
          )}

          {propuesta.data && parcial && (
            <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
              Esta orden ya tiene pagos: faltan{" "}
              <strong className="tabular-nums">{monto_(propuesta.data.monto_pendiente)}</strong>{" "}
              {propuesta.data.divisa_orden} de {monto_(propuesta.data.monto_orden)}.
            </p>
          )}

          {/* Con PIN, se abre AQUÍ: sin cambiar de pestaña ni cerrar el pago (19/10). */}
          <PinEnLinea error={propuesta.error} />
          {propuesta.error instanceof ErrorApi && !esCuentaBloqueada(propuesta.error) && (
            <p className="text-sm" style={{ color: "var(--destructive)" }}>
              {propuesta.error.message}
            </p>
          )}

          {/* ⚠ **Una sola sección, no dos.** La conversión y los importes son la MISMA
            operación: en dos recuadros se leían como dos formularios dentro del
            modal, y con ellos la duda de cuál de los dos manda. Manda el importe
            debitado; lo de arriba es la forma de llegar a él. */}
          {propuesta.data && (
            <fieldset className="tarjeta space-y-4 p-4">
              <legend className="px-1 text-sm font-medium">Lo que sale del banco</legend>
              {fondos.aviso}
              <p className="text-sm">
                Saldo disponible:{" "}
                <strong className="tabular-nums">
                  {comoMonto(aCentimos(propuesta.data.saldo_disponible))}
                </strong>{" "}
                {moneda}
                {propuesta.data.tasa_aplicada && (
                  <>
                    {" "}
                    · la orden es en {propuesta.data.divisa_orden}: el monto se convirtió a la tasa
                    del {comoFecha(fecha)}. Corrígelo con lo que debitó el banco.
                  </>
                )}
                {mismaMoneda && (
                  <>
                    {" "}
                    · la orden y la cuenta son en {moneda}: se debita el neto de la orden, sin
                    conversión.
                  </>
                )}
              </p>

              {!mismaMoneda && (
                <Conversor
                  monedas={monedasDeTasas(tasas.data ?? [])}
                  principal={propuesta.data.divisa_cuenta}
                  referenciaInicial={propuesta.data.divisa_orden}
                  valor={monto}
                  onCambiar={(v) => {
                    setMonto(v)
                    montoTocado.current = true
                  }}
                  // ⚠ **El importe de referencia ES lo que el pago cubre de la orden**, el
                  // mismo dato del campo de abajo. Eran dos números independientes: el
                  // conversor enseñaba una tasa del BCV y el servidor guardaba otra
                  // —`monto_debitado / monto_equivalente`—, así que la pantalla decía una
                  // cosa y el registro otra.
                  equivalenteValor={equivalente}
                  onEquivalente={(v) => {
                    setEquivalente(v)
                    montoTocado.current = true
                  }}
                  enmarcado={false}
                  nota={
                    propuesta.data.divisa_orden === propuesta.data.divisa_cuenta
                      ? "Teclea el importe o la tasa: lo otro se recalcula."
                      : "Corrige la tasa con la del comprobante: lo debitado no se mueve y se ajusta cuánto cubre de la orden. Es la tasa que queda guardada con el pago."
                  }
                />
              )}

              <div className="grid gap-4 sm:grid-cols-3">
                <Campo para="pago-monto" etiqueta={`Monto debitado ${moneda}`}>
                  <input
                    id="pago-monto"
                    value={monto}
                    onChange={(e) => {
                      setMonto(e.target.value)
                      montoTocado.current = true
                    }}
                    inputMode="decimal"
                    className="campo mt-1 tabular-nums"
                  />
                </Campo>
                <Campo
                  para="pago-comision"
                  etiqueta="Comisión bancaria"
                  ayuda={
                    recalculo.isFetching
                      ? "Recalculando con el nuevo monto…"
                      : "Sale de la tarifa del método y sigue al monto. Corrígela con la que debitó el banco."
                  }
                >
                  <input
                    id="pago-comision"
                    value={comision}
                    onChange={(e) => setComision(e.target.value)}
                    inputMode="decimal"
                    className="campo mt-1 tabular-nums"
                  />
                </Campo>
                <Campo para="pago-igtf" etiqueta="IGTF">
                  <input
                    id="pago-igtf"
                    value={aplicaIgtf ? igtf : "0"}
                    onChange={(e) => setIgtf(e.target.value)}
                    disabled={!aplicaIgtf}
                    inputMode="decimal"
                    className="campo mt-1 tabular-nums disabled:opacity-60"
                  />
                </Campo>
              </div>

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={aplicaIgtf}
                  onChange={(e) => setAplicaIgtf(e.target.checked)}
                />
                Aplica IGTF (pagos en divisas)
              </label>

              {/* ⚠ **El hueco que esto cierra se midió**: en 8 de cada 10 pagos con
                comisión entraron los bolívares del pago y salieron los del pago MÁS la
                comisión, así que el banco cobró algo que nunca entró en la cuenta.
                Sólo aparece cuando hay un cambio de recursos vivo que continuar: si el
                pago se financió con fondos que ya estaban, no hay nada de dónde
                traerlo y traerlo de una cuenta elegida a ojo sería inventar una
                operación. */}
              {puedeTraerComision && (
                <label className="flex items-start gap-2 text-sm" htmlFor="pago-traer-comision">
                  <input
                    id="pago-traer-comision"
                    type="checkbox"
                    className="mt-0.5"
                    checked={traerComision}
                    onChange={(e) => setTraerComision(e.target.checked)}
                  />
                  <span>
                    Traer también la comisión desde{" "}
                    <strong>{propuesta.data.cuenta_comision}</strong>:{" "}
                    <strong className="tabular-nums">
                      {monto_(propuesta.data.comision_a_traer ?? "0")}
                    </strong>{" "}
                    {propuesta.data.divisa_comision}
                    <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
                      A la tasa del cambio que financió esta orden. Sin esto, la cuenta paga una
                      comisión que nunca entró.
                    </span>
                  </span>
                </label>
              )}

              {/* ⚠ **Sólo en las cuentas CONDUCTO**, las que no llevan saldo propio: se
                paga con dinero que llega de fuera en el momento y su disponible real
                no se conoce aquí. Sin esto el extracto las enseña siempre en negativo,
                con salidas que ninguna entrada explica. En una cuenta normal la
                casilla no aparece: inventar una entrada ahí es justo lo que el
                control de saldo existe para impedir. */}
              {propuesta.data.fondeo_automatico && (
                <label className="flex items-start gap-2 text-sm" htmlFor="pago-con-fondeo">
                  <input
                    id="pago-con-fondeo"
                    type="checkbox"
                    className="mt-0.5"
                    checked={conFondeo}
                    onChange={(e) => setConFondeo(e.target.checked)}
                  />
                  <span>
                    Registrar también el <strong>ingreso que fondea este pago</strong>
                    <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
                      Esta cuenta no guarda saldo: el dinero entra y sale el mismo día. La entrada
                      se anula sola si el pago se revierte.
                    </span>
                  </span>
                </label>
              )}

              {!mismaMoneda && (
                <Campo
                  para="pago-equivalente"
                  etiqueta={`Cubre de la orden (${propuesta.data.divisa_orden})`}
                  ayuda={`Pendiente: ${monto_(propuesta.data.monto_pendiente)} ${propuesta.data.divisa_orden}. Si el pago no lo cubre entero, teclea cuánto cubre.`}
                >
                  <input
                    id="pago-equivalente"
                    value={equivalente}
                    onChange={(e) => {
                      setEquivalente(e.target.value)
                      montoTocado.current = true
                    }}
                    inputMode="decimal"
                    className="campo mt-1 tabular-nums"
                  />
                </Campo>
              )}

              {diferencia !== null && (
                <DiferenciaPago
                  diferencia={diferencia}
                  moneda={propuesta.data.divisa_orden}
                  valor={tratamiento}
                  onCambiar={setTratamiento}
                />
              )}

              {total !== null && (
                <p className="text-sm">
                  Sale de la cuenta: <strong className="tabular-nums">{comoMonto(total)}</strong>{" "}
                  {moneda}
                  {/* Lo que entra antes del débito se dice, o el saldo de arriba no
                    cuadraría con «sale» y parecería que falta dinero. */}
                  {entraDeOtraCuenta > 0n && (
                    <span style={{ color: "var(--fg-muted)" }}>
                      {" "}
                      · antes entran {comoMonto(entraDeOtraCuenta)} {moneda} de{" "}
                      {propuesta.data?.cuenta_comision}
                    </span>
                  )}
                  {/* Corto y con sobregiro no es un error: es la cuenta de la que se
                    adelanta el gasto. Se dice cuánto va a quedar debiendo, porque
                    pagar y quedar en rojo sin enterarse es lo que el aviso evita. */}
                  {corto &&
                    disponible !== null &&
                    (sobregiro ? (
                      <span style={{ color: "var(--warning)" }}>
                        {" "}
                        — la cuenta queda en {comoMonto(disponible - total)} {moneda}; admite
                        sobregiro
                      </span>
                    ) : (
                      <span style={{ color: "var(--destructive)" }}> — no alcanza el saldo</span>
                    ))}
                </p>
              )}
            </fieldset>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <Campo para="pago-referencia" etiqueta="Referencia bancaria">
              <input
                id="pago-referencia"
                value={referencia}
                onChange={(e) => setReferencia(e.target.value)}
                maxLength={60}
                className="campo mt-1"
              />
            </Campo>
            <Campo para="pago-fecha" etiqueta="Fecha valor">
              <input
                id="pago-fecha"
                type="date"
                value={fecha}
                max={hoy()}
                onChange={(e) => setFecha(e.target.value)}
                className="campo mt-1"
              />
            </Campo>
          </div>
          <fieldset>
            <legend className="text-sm font-medium">Comprobante del banco</legend>
            <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
              {sinComprobante
                ? "Este pago se registra sin comprobante: hace falta decir por qué."
                : "Obligatorio. Arrástralo, pégalo con Ctrl+V o elígelo."}
            </p>

            {/* ⚠ **Sólo con el permiso, y no levanta la regla: la cambia de forma.** Hay
              movimientos que no producen comprobante —un cruce de fondos entre cuentas
              propias— y exigir un archivo ahí obliga a subir una captura que no prueba
              nada. Lo que no se admite es un pago sin ninguna de las dos cosas: sin
              archivo hay que escribir el motivo, y el servidor lo vuelve a exigir. */}
            {puedeSinComprobante && (
              <label className="mt-2 flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={sinComprobante}
                  onChange={(e) => {
                    setSinComprobante(e.target.checked)
                    if (e.target.checked) setComprobante([])
                  }}
                  className="mt-1"
                />
                <span>
                  <span className="block font-medium">Este pago no tiene comprobante</span>
                  <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
                    Para cruces de fondos entre cuentas propias y compensaciones, que no producen
                    uno.
                  </span>
                </span>
              </label>
            )}

            {sinComprobante && (
              <Campo
                para="pago-sin-comprobante"
                etiqueta="Por qué no hay comprobante"
                ayuda="Queda guardado con el pago y se lee en la ficha de la orden."
              >
                <input
                  id="pago-sin-comprobante"
                  value={motivoSinComprobante}
                  onChange={(e) => setMotivoSinComprobante(e.target.value)}
                  maxLength={300}
                  placeholder="Cruce de fondos entre Binance y BBVA, sin comprobante bancario"
                  className="campo mt-1"
                />
              </Campo>
            )}

            {!sinComprobante && (
              <AdjuntosNuevos
                adjuntos={comprobante}
                rechazados={rechazados}
                etiqueta="Adjuntar comprobante"
                multiple={false}
                // Uno solo: el que llega sustituye al anterior, que es lo que se
                // espera al arrastrar el archivo bueno encima del equivocado.
                onArchivos={(archivos) => {
                  const siguiente = sumar([], archivos.slice(0, 1))
                  setComprobante(siguiente.adjuntos)
                  setRechazados(siguiente.rechazados)
                }}
                onQuitar={() => setComprobante([])}
              />
            )}
          </fieldset>
          {puedeLeer && comprobante[0] && (
            <LecturaComprobante
              archivo={comprobante[0].archivo}
              ordenId={orden.id}
              divisaCuenta={propuesta.data?.divisa_cuenta ?? null}
              onUsar={usarLectura}
            />
          )}

          <PinEnLinea error={ejecutar.error} onAbierta={() => ejecutar.reset()} />
          {error && (
            <p className="text-sm" style={{ color: "var(--destructive)" }}>
              {error}
            </p>
          )}

          <div className="flex justify-end gap-2">
            <Boton onClick={onCerrar}>Cancelar</Boton>
            <Boton type="submit" variante="primario" disabled={!listo || ejecutar.isPending}>
              {ejecutar.isPending ? "Pagando…" : "Ejecutar pago"}
            </Boton>
          </div>
        </form>
      </Modal>
    </>
  )
}
